/**
 * PostBuildActions — what a finished change offers, as buttons: "Save to my branch" and "Validate against org".
 *
 * SERVER-DRIVEN. The set of buttons is `TaskSnapshot.actions` (contracts 0.2.30), in the server's order: the GUI
 * never decides from the task state or the deploy mode what is offered, so the card, the task panel and the chat seat
 * can never disagree. A button also needs its handler (a host with no save wiring shows no Save button, the same
 * rule as `onCancelTask` / `onRevert`). `actions === undefined` is an older server that predates the field: it falls
 * back to Save alone when the host can save, as before.
 *
 * BOTH ARE RE-RUNNABLE. Save opens the host's save form (`onSave`, a toggle); Validate mints a FRESH idempotency key
 * for every click (the same key would replay the old check, never run a new one), shows "Checking..." while the
 * request is in flight and while the task itself reports `checking` (then, with no Validate offered, as a status line), and maps each typed refusal to one fixed line.
 *
 * Exported on its own (plus `describeValidateFailure`, `visibleActions`) so the center chat can mount the same row.
 */
import { useRef, useState } from "react";
import { CheckCircle2, GitBranch, Loader2, MessageSquarePlus, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { describeLaneV2Error, FREE_TEXT_REQUIRED_COPY, isLaneV2Error, LANE_V2_OFFLINE_COPY, newActionKey, newValidateKey } from "@/services/multi-agent/laneV2/api";
import type { TaskAction } from "@/services/multi-agent/laneV2/types";
import { rechecksLeftText } from "./rechecks";

/** The request-changes text bound (MSA `RequestChangesIn.text` max_length). */
export const REQUEST_CHANGES_MAX = 4000;

/** Every action this component renders; any other server action is ignored (`visibleActions`). */
export const ACTION_LABEL: Record<"save" | "validate" | "request_changes" | "discard", string> = {
  save: "Save to my branch",
  validate: "Validate against org",
  request_changes: "Request changes",
  discard: "Discard",
};

export const DISCARD_SAVE_RUNNING_COPY = "Discard is unavailable while a save is running.";
export const DISCARD_CONFIRM_COPY = "Discard this change? The task ends; anything already saved to a branch stays there.";

/** The actions to show, in the server's order, limited to the ones the host can actually run. */
export function visibleActions(
  actions: readonly string[] | undefined,
  can: { save: boolean; validate: boolean; request_changes?: boolean; discard?: boolean },
): TaskAction[] {
  const offered: readonly string[] = actions ?? (can.save ? ["save"] : []);
  const out: TaskAction[] = [];
  for (const a of offered) {
    if (a !== "save" && a !== "validate" && a !== "request_changes" && a !== "discard") continue;
    if (!can[a] || out.includes(a)) continue;
    out.push(a);
  }
  return out;
}

/** One fixed line per refusal; an untyped failure gets a generic line. Never the server's own text. */
export function describeValidateFailure(err: unknown): string {
  if (isLaneV2Error(err)) {
    // 409 DEPLOY_NOT_ENABLED per the contract; PACKAGE_DEPLOY_NOT_ENABLED (503) is what a server that does not translate it sends.
    if (err.code === "DEPLOY_NOT_ENABLED" || err.code === "PACKAGE_DEPLOY_NOT_ENABLED") return "Checking against the org is turned off in this environment. You can still save your change to a branch.";
    return describeLaneV2Error(err);
  }
  return "The check could not be started. Nothing was changed. You can try again.";
}

/** One fixed line per refusal of Request changes / Discard; an untyped failure gets a generic line. Never the server's own text. */
export function describeRestActionFailure(err: unknown): string {
  if (isLaneV2Error(err)) return describeLaneV2Error(err);
  return "That did not work. Nothing was changed. You can try again.";
}

/**
 * One idempotency key per user INTENT. A key is minted when the intent starts, REUSED by a retry after a failed send
 * (the server replays instead of doing it twice), and cleared on success, Cancel, or (Request changes) an edit of the text,
 * so the next intent gets a fresh key. Pure so it is testable without a DOM.
 */
export type ActionKeyKind = "validate" | "rc" | "dc";
export class ActionKeys {
  private keys: Partial<Record<ActionKeyKind, string>> = {};
  ensure(kind: ActionKeyKind): string {
    return (this.keys[kind] ??= kind === "validate" ? newValidateKey() : newActionKey(kind));
  }
  clear(kind: ActionKeyKind): void {
    delete this.keys[kind];
  }
}

/** Send with the intent's key: cleared on success, kept on failure (the caller's retry reuses it). Rethrows the failure. */
export async function sendKeyed<T>(keys: ActionKeys, kind: ActionKeyKind, send: (key: string) => Promise<T>): Promise<T> {
  const out = await send(keys.ensure(kind));
  keys.clear(kind);
  return out;
}

export interface PostBuildActionsProps {
  /** `TaskSnapshot.actions`. */
  actions: readonly string[] | undefined;
  /** `TaskSnapshot.state`: `checking` means a check is already running, so Validate shows as in progress. */
  taskState?: string;
  /** Toggle the host's save form. Absent: no Save button. */
  onSave?: () => void;
  saveOpen?: boolean;
  /** Re-checks remaining (`rechecksLeft(transitions)`). Shown beside Validate; at 0 the button is gone and this says why. Absent: nothing shown. */
  rechecksLeft?: number;
  /** R0 `offline`: Request changes and Validate are refused (shown disabled with the offline line); Discard still works. */
  offline?: boolean;
  /** R3 `branch_save_running != null`: Discard does not stop a running save (chunks keep landing), so it is refused meanwhile. */
  saveRunning?: boolean;
  /** Run one check with this fresh key. Absent: no Validate button. Rejects with `LaneV2Error` on refusal. */
  onValidate?: (idempotencyKey: string) => Promise<unknown>;
  /** Send the change request text with a fresh key. Absent: no Request changes button. Rejects with `LaneV2Error` on refusal. */
  onRequestChanges?: (text: string, idempotencyKey: string) => Promise<unknown>;
  /** Discard the resting changeset with a fresh key (after the confirm step). Absent: no Discard button. */
  onDiscard?: (idempotencyKey: string) => Promise<unknown>;
  className?: string;
}

export function PostBuildActions({ actions, taskState, onSave, saveOpen, rechecksLeft, offline = false, saveRunning = false, onValidate, onRequestChanges, onDiscard, className }: PostBuildActionsProps) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const inFlight = useRef(false);
  const [panel, setPanel] = useState<"changes" | "discard" | null>(null);
  const [text, setText] = useState("");
  const keys = useRef(new ActionKeys());
  const shown = visibleActions(actions, { save: !!onSave, validate: !!onValidate, request_changes: !!onRequestChanges, discard: !!onDiscard });
  const taskChecking = taskState === "checking";
  const noRechecksLeft = taskState === "validated" && rechecksLeft === 0 && !shown.includes("validate");
  // The server drops `discard` from actions while a save runs; the note still says why there is no Discard (a Discard handler means the task can be discarded at rest).
  const discardNote = saveRunning && !!onDiscard;
  if (shown.length === 0 && !taskChecking && !noRechecksLeft && !discardNote) return null;

  const running = busy || taskChecking;
  const offlineRefused = offline && (shown.includes("validate") || shown.includes("request_changes"));

  const validate = async () => {
    if (!onValidate || inFlight.current || offline) return;
    inFlight.current = true;
    setBusy(true);
    setMessage(null);
    try {
      await sendKeyed(keys.current, "validate", (k) => onValidate(k));
    } catch (err) {
      setMessage(describeValidateFailure(err));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const trimmed = text.trim();
  const sendChanges = async () => {
    if (!onRequestChanges || inFlight.current || offline) return;
    if (!trimmed) {
      setMessage(FREE_TEXT_REQUIRED_COPY);
      return;
    }
    inFlight.current = true;
    setBusy(true);
    setMessage(null);
    try {
      await sendKeyed(keys.current, "rc", (k) => onRequestChanges(trimmed.slice(0, REQUEST_CHANGES_MAX), k));
      setText("");
      setPanel(null);
    } catch (err) {
      setMessage(describeRestActionFailure(err));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  const confirmDiscard = async () => {
    if (!onDiscard || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setMessage(null);
    try {
      await sendKeyed(keys.current, "dc", (k) => onDiscard(k));
      setPanel(null);
    } catch (err) {
      setMessage(describeRestActionFailure(err));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  const togglePanel = (which: "changes" | "discard") => {
    setMessage(null);
    // Closing a panel (Cancel / Keep it / the toggle) ends that intent: its key is not reused by the next one.
    keys.current.clear(which === "changes" ? "rc" : "dc");
    setPanel((p) => (p === which ? null : which));
  };

  // Save is the one primary action (accent); Validate is the quiet one. 13px, the size of an action, not of a caption.
  const base = "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors disabled:opacity-50";
  const primaryClass = cn(base, "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90");
  const destructiveClass = cn(base, "bg-destructive-strong text-white hover:bg-destructive-strong/90");
  const buttonClass = cn(base, "border border-border text-foreground hover:bg-muted/50");

  return (
    <div data-lane-v2-post-build-actions="" className={cn("space-y-1.5", className)}>
      <div className="flex flex-wrap items-center gap-2">
        {shown.map((a) =>
          a === "save" ? (
            <button
              key="save"
              type="button"
              onClick={onSave}
              aria-expanded={!!saveOpen}
              data-lane-v2-action="save"
              className={primaryClass}
            >
              <GitBranch className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {ACTION_LABEL.save}
            </button>
          ) : a === "request_changes" ? (
            <button
              key="request_changes"
              type="button"
              onClick={() => togglePanel("changes")}
              disabled={busy || offline}
              aria-expanded={panel === "changes"}
              data-lane-v2-action="request_changes"
              className={buttonClass}
            >
              <MessageSquarePlus className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {ACTION_LABEL.request_changes}
            </button>
          ) : a === "discard" ? (
            <button
              key="discard"
              type="button"
              onClick={() => togglePanel("discard")}
              disabled={busy || saveRunning}
              aria-expanded={panel === "discard"}
              data-lane-v2-action="discard"
              className={buttonClass}
            >
              <Trash2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {ACTION_LABEL.discard}
            </button>
          ) : (
            <button
              key="validate"
              type="button"
              onClick={() => void validate()}
              disabled={running || offline}
              data-lane-v2-action="validate"
              className={buttonClass}
            >
              {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden />}
              {running ? "Checking against the org..." : ACTION_LABEL.validate}
            </button>
          ),
        )}
        {shown.includes("validate") && rechecksLeft !== undefined && rechecksLeft > 0 && !running && (
          <span data-lane-v2-rechecks-left="" className="text-[12px] text-muted-foreground">
            {rechecksLeftText(rechecksLeft)}
          </span>
        )}
        {noRechecksLeft && (
          <span data-lane-v2-rechecks-left="" className="text-[12px] text-muted-foreground">
            {rechecksLeftText(0)}
          </span>
        )}
      </div>
      {panel === "changes" && shown.includes("request_changes") && (
        <div data-lane-v2-request-changes-form="" className="space-y-1.5">
          <textarea
            value={text}
            onChange={(e) => {
              // New text is a new request: it must not replay the key of an earlier failed send of other text.
              keys.current.clear("rc");
              setText(e.target.value.slice(0, REQUEST_CHANGES_MAX));
            }}
            maxLength={REQUEST_CHANGES_MAX}
            rows={3}
            disabled={busy}
            aria-label="Describe the change you want"
            placeholder="Describe the change you want"
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[13px]"
          />
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => void sendChanges()} disabled={busy || !trimmed} data-lane-v2-request-changes-send="" className={primaryClass}>
              {busy ? "Sending..." : "Send"}
            </button>
            <button type="button" onClick={() => togglePanel("changes")} disabled={busy} className={buttonClass}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {panel === "discard" && shown.includes("discard") && (
        <div data-lane-v2-discard-confirm="" role="alertdialog" aria-modal="false" aria-label="Confirm discard" className="space-y-1.5">
          <p className="text-[13px] text-foreground">{DISCARD_CONFIRM_COPY}</p>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => void confirmDiscard()} disabled={busy || saveRunning} data-lane-v2-discard-confirm-yes="" className={destructiveClass}>
              {busy ? "Discarding..." : "Discard"}
            </button>
            {/* Focus lands on the safe choice when the dialog opens; the destructive confirm needs a deliberate Tab + click. */}
            <button type="button" autoFocus onClick={() => togglePanel("discard")} disabled={busy} data-lane-v2-discard-keep="" className={buttonClass}>
              Keep it
            </button>
          </div>
        </div>
      )}
      {/* The server offers Validate only at rest, so while the task itself is checking there is no button to carry the progress. */}
      {taskChecking && !shown.includes("validate") && (
        <p role="status" data-lane-v2-checking="" className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
          Checking against the org...
        </p>
      )}
      {discardNote && (
        <p role="status" data-lane-v2-discard-save-note="" className="text-[11px] text-muted-foreground">
          {DISCARD_SAVE_RUNNING_COPY}
        </p>
      )}
      {offlineRefused && (
        <p role="status" data-lane-v2-offline-note="" className="text-[11px] text-muted-foreground">
          {LANE_V2_OFFLINE_COPY}
        </p>
      )}
      {message && (
        <p role="status" data-lane-v2-validate-message="" className="text-[11px] text-ma-err-text">
          {message}
        </p>
      )}
    </div>
  );
}
